import { cn } from "cn";
import type { ReactNode } from "react";
import { toast as sonnerToast } from "sonner";
import { CloseButton } from "../button/close-button";
import { ToastContent } from "./toast-content";
import {
  ErrorIcon,
  InfoIcon,
  LoadingIcon,
  SuccessIcon,
  WarningIcon,
} from "./toast-icons";
import type {
  ToastColorScheme,
  ToastCustomOptions,
  ToastDismissFunction,
  ToastLoadingOptions,
  ToastOptions,
  ToastPromiseOptions,
  ToastVariant,
} from "./types";

const DEFAULT_DURATION = 3000;
/** A toast with a button (an Undo) stays long enough to find and press it. */
const ACTION_DURATION = 6000;
const LOADING_DURATION = Number.POSITIVE_INFINITY;

function getPosition() {
  const isMobile = typeof window !== "undefined" && window.innerWidth < 576;
  return isMobile ? "bottom-center" : "bottom-left";
}

function getIcon(variant: ToastVariant): ReactNode {
  switch (variant) {
    case "success":
      return <SuccessIcon />;
    case "error":
      return <ErrorIcon />;
    case "warning":
      return <WarningIcon />;
    case "info":
      return <InfoIcon />;
  }
}

const customSchemeClasses: Record<ToastColorScheme, string> = {
  dark: "bg-bg-dark-max border-border-dark-min/4 backdrop-blur-xl text-text-light",
  light: "bg-bg-light border-border-dark-low shadow-lg text-text-dark-max",
};

function createToast(
  variant: ToastVariant,
  options: ToastOptions,
): string | number {
  const {
    title,
    description,
    action,
    duration = action ? ACTION_DURATION : DEFAULT_DURATION,
    closable = true,
    colorScheme = "dark",
    onDismiss,
    onAutoClose,
  } = options;

  return sonnerToast.custom(
    (t) => (
      <ToastContent
        icon={getIcon(variant)}
        title={title}
        description={description}
        closable={closable}
        action={action}
        colorScheme={colorScheme}
        onDismiss={() => sonnerToast.dismiss(t)}
      />
    ),
    {
      position: getPosition(),
      duration,
      onDismiss,
      onAutoClose,
    },
  );
}

export const toast = {
  success: (options: ToastOptions): string | number =>
    createToast("success", options),
  error: (options: ToastOptions): string | number =>
    createToast("error", options),
  warning: (options: ToastOptions): string | number =>
    createToast("warning", options),
  info: (options: ToastOptions): string | number =>
    createToast("info", options),

  loading: (options: ToastLoadingOptions): ToastDismissFunction => {
    const { title, colorScheme = "dark" } = options;
    const iconColor =
      colorScheme === "light" ? "text-text-dark-max" : "text-text-light";
    const id = sonnerToast.custom(
      () => (
        <ToastContent
          icon={<LoadingIcon className={iconColor} />}
          title={title}
          closable={false}
          colorScheme={colorScheme}
          onDismiss={() => {}}
        />
      ),
      {
        position: getPosition(),
        duration: LOADING_DURATION,
      },
    );
    return () => sonnerToast.dismiss(id);
  },

  promise: async <T,>(
    promise: Promise<T>,
    options: ToastPromiseOptions<T>,
  ): Promise<T> => {
    const loadingId = toast.loading({ title: options.loading });
    try {
      const result = await promise;
      loadingId();
      const successMessage =
        typeof options.success === "function"
          ? options.success(result)
          : options.success;
      toast.success({ title: successMessage });
      return result;
    } catch (error) {
      loadingId();
      const errorMessage =
        typeof options.error === "function"
          ? options.error(error as Error)
          : options.error;
      toast.error({ title: errorMessage });
      throw error;
    }
  },

  custom: (
    content: ReactNode,
    options?: ToastCustomOptions,
  ): string | number => {
    const {
      duration = DEFAULT_DURATION,
      closable = true,
      colorScheme = "dark",
      onDismiss,
      onAutoClose,
    } = options || {};
    return sonnerToast.custom(
      (t) => (
        <div
          className={cn(
            /* sorted */
            "flex items-start gap-3",
            "p-3",
            "border rounded-xl",
            customSchemeClasses[colorScheme],
          )}
        >
          <div className="flex-1">{content}</div>
          {closable && (
            <CloseButton
              size="xs"
              colorScheme={colorScheme === "light" ? "dark" : "light"}
              onClick={() => sonnerToast.dismiss(t)}
            />
          )}
        </div>
      ),
      {
        position: getPosition(),
        duration,
        onDismiss,
        onAutoClose,
      },
    );
  },

  dismiss: (id?: string | number): void => {
    sonnerToast.dismiss(id);
  },
};
